/**
 * Enhanced Frontend Error Tracking Service for myboki.me App v0.6.5
 * Captures detailed Chrome Console errors and sends structured data to backend
 */


class ErrorTrackingService {
    constructor() {
        this.apiKey = process.env.REACT_APP_ERROR_TRACKING_API_KEY || 'dev-error-key';
        this.baseURL = process.env.REACT_APP_API_BASE_URL || 'https://api.deinbalu.de';
        this.endpoint = `${this.baseURL}/error-tracking`;
        this.userId = null;
        this.sessionId = this.generateSessionId();
        this.isInitialized = false;
        this.isDev = (process.env.NODE_ENV !== 'production' || window.location.hostname === 'localhost');
        // No console output to avoid leaking endpoints in production
    }

    /**
     * Initialize error tracking (call once in App.js)
     */
    initialize() {
        if (this.isInitialized) return;
        
        this.initializeErrorHandlers();
        this.interceptFetchAPI();
        this.isInitialized = true;
        // no console output
    }

    /**
     * Generate unique session ID
     */
    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Set current user ID for error context
     */
    setUserId(userId) {
        // Store only opaque/non-PII identifiers. Ignore raw emails or obvious PII.
        if (typeof userId === 'string' && userId.includes('@')) {
            this.userId = null;
            return;
        }
        this.userId = userId || null;
        // No console output
    }

    /**
     * Initialize global error handlers
     */
    initializeErrorHandlers() {
        // Catch unhandled JavaScript errors
        window.addEventListener('error', (event) => {
            this.trackJavaScriptError(event);
        });

        // Catch unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.trackPromiseRejection(event);
        });
    }

    /**
     * Track JavaScript errors
     */
    trackJavaScriptError(event) {
        const errorData = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            method: 'GET',
            isMobile: this.isMobile(),
            error: event.message || 'JavaScript Error',
            networkOnline: navigator.onLine,
            type: 'JAVASCRIPT_ERROR'
        };

        this.sendErrorToBackend(errorData);
    }

    /**
     * Track promise rejections
     */
    trackPromiseRejection(event) {
        const errorData = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            method: 'PROMISE',
            isMobile: this.isMobile(),
            error: event.reason?.message || event.reason?.toString() || 'Promise rejected',
            networkOnline: navigator.onLine,
            type: 'PROMISE_REJECTION'
        };

        this.sendErrorToBackend(errorData);
    }

    /**
     * Intercept fetch API to catch HTTP errors
     */
    interceptFetchAPI() {
        const originalFetch = window.fetch;
        const self = this;
        
        window.fetch = async function(...args) {
            try {
                const response = await originalFetch.apply(this, args);
                
                // Track HTTP errors
                if (!response.ok) {
                    const url = typeof args[0] === 'string' ? args[0] : args[0].url;
                    const method = args[1]?.method || 'GET';
                    
                    self.trackHttpError(response.status, url, method);
                }
                
                return response;
            } catch (error) {
                // Track network errors
                const url = typeof args[0] === 'string' ? args[0] : args[0].url;
                const method = args[1]?.method || 'GET';
                
                self.trackNetworkError(error, url, method);
                throw error; // Re-throw to maintain original behavior
            }
        };
    }

    /**
     * Track HTTP errors (400, 404, 500, etc.)
     */
    trackHttpError(status, url, method) {
        // Prevent infinite loop - don't track errors from the error tracking endpoint
        if (url && url.includes('/error-tracking')) {
            return;
        }

        const errorData = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            method: method.toUpperCase(),
            isMobile: this.isMobile(),
            error: `HTTP ${status} Error: ${this.getHttpStatusMessage(status)} - ${this.extractEndpoint(url)}`,
            networkOnline: navigator.onLine,
            type: 'HTTP_ERROR',
            status
        };

        this.sendErrorToBackend(errorData);
        // no console output
    }

    /**
     * Track network errors
     */
    trackNetworkError(error, url, method) {
        // Prevent infinite loop - don't track errors from the error tracking endpoint
        if (url && url.includes('/error-tracking')) {
            return;
        }

        const errorData = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            method: method.toUpperCase(),
            isMobile: this.isMobile(),
            error: `Network Error: ${error.message} - ${this.extractEndpoint(url)}`,
            networkOnline: navigator.onLine,
            type: 'NETWORK_ERROR'
        };

        this.sendErrorToBackend(errorData);
        // no console output
    }

    /**
     * Manual error tracking for specific application errors
     */
    trackCustomError(errorType, errorMessage, context = {}) {
        const errorData = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            method: context.method || 'CUSTOM',
            isMobile: this.isMobile(),
            error: `${errorType}: ${errorMessage}`,
            networkOnline: navigator.onLine,
            type: errorType
        };

        this.sendErrorToBackend(errorData);
        // no console output
    }

    /**
     * Send error data to backend
     */
    async sendErrorToBackend(errorData) {
        // Honor user consent and Do Not Track
        if (navigator.doNotTrack === '1') return;
        let consent = false;
        try { consent = localStorage.getItem('consent.errorTracking') === 'granted'; } catch { consent = false; }
        if (!consent) return;

        // Optionally skip in development (silent)
        if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
            return;
        }

        const sanitize = (data) => {
            const toPath = (u) => {
                try { const { pathname } = new URL(u, window.location.origin); return pathname; } catch { return '/'; }
            };
            const message = String(data.error || '').slice(0, 500);
            return {
                timestamp: new Date().toISOString(),
                type: data.type,
                method: (data.method || 'GET').toUpperCase(),
                path: toPath(data.url || window.location.href),
                status: data.status,
                isMobile: !!data.isMobile,
                networkOnline: !!data.networkOnline,
                message,
                sessionId: String(this.sessionId).slice(0, 64)
            };
        };

        const payload = sanitize(errorData);

        try {
            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Error-Tracking-Key': this.apiKey
                },
                body: JSON.stringify(payload)
            });

            // Silent in production; do not console.log/warn
        } catch (_) {
            // Silent fail - avoid loops and logs
        }
    }

    // Utility methods
    isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    extractEndpoint(url) {
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            return url || 'unknown';
        }
    }

    getHttpStatusMessage(status) {
        const statusMessages = {
            400: 'Bad Request',
            401: 'Unauthorized',
            403: 'Forbidden',
            404: 'Not Found',
            500: 'Internal Server Error',
            502: 'Bad Gateway',
            503: 'Service Unavailable'
        };
        return statusMessages[status] || 'Unknown Error';
    }
}

// Create singleton instance
const errorTrackingService = new ErrorTrackingService();

export default errorTrackingService;